<template>
  <div class="col-12">
      <!-- Header of collapsable item -->
      <div class="col-12 d-flex justify-content-between align-items-center pb-1">
          <div class="flex-grow-1">
              <h4 class="form-header pointer hover-header" @click.stop="materialsVisible = !materialsVisible">
                  {{ $t('material_reports.title') }} ({{results.length}})
              </h4>
          </div>
          <div
              v-if="loading"
              id="loader"
              class="spinner-section"
          />
          <div v-else class="ms-2 px-2" @click.stop="materialsVisible = !materialsVisible">
              <font-awesome-icon icon="arrow-down" class="pointer no-focus arrow-icon dont-print hover-header" :class="{ rotated:materialsVisible }"/>
          </div>
      </div>
      <!-- Collapsable content -->
      <div class="routa-collapse" :class="{ collapsed: materialsVisible }">
          <!-- Summary -->
          <div class="col-12 my-2">
            <contract-material-summary
              :results="results"
              @show-add-material-report="showAddMaterialReportModal"
              @download-material-report-excel="downloadMaterialReportExcel"
            />
          </div>

          <Transition name="fade">
            <div v-if="results.length > 0" class="list-content result-table pointer my-3">
                <vue3-datatable
                    ref="resultTable"
                    :rows="results"
                    :columns="fields"
                    :pagination="false"
                    :sticky-header="true"
                    height="50vh"
                    :sortable="true"
                    skin="bh-table-compact"
                    row-class="routa-table-row"
                    cell-class="routa-table-cell"
                    @row-click="editMaterialReportItem"
                >
                    <template #date="row">
                        {{ getShortDateString(row.value.date) }}
                    </template>
                </vue3-datatable>
            </div>
          </Transition>
      </div>
      <hr class="report-divider nopads">
      <base-modal v-if="materialReport !== null" size="lg" hide-footer hide-header @close="materialReport = null">
        <contract-material-report-editor
          :material-report="materialReport"
          @emit-add-material-report="submitMaterialReport"
          @close="closeEditor"
        />
    </base-modal>
  </div>
</template>

<script>
import {timeUtils} from "@/components/mixins/TimeUtils";
import {downloadHelper} from "@/components/mixins/DownloadMixin";
import {restApi} from "@/components/mixins/RestApiMixin";
import {materialReportHelper} from "@/components/mixins/MaterialReportMixin";
import ContractMaterialReportEditor from "@/components/contractreport/ContractMaterialReportEditor.vue";
import ContractMaterialSummary from "@/components/contractreport/ContractMaterialSummary.vue";
import BaseModal from "@/components/BaseModal";
import Vue3Datatable from "@bhplugin/vue3-datatable";
import {inject} from "vue";

export default {
    name: "ContractMaterialReports",
    components: {BaseModal, ContractMaterialSummary, ContractMaterialReportEditor, Vue3Datatable},
    mixins: [timeUtils, restApi, materialReportHelper, downloadHelper],
    props: {
        searchParams: {
            type: Object,
            default() {
                return null
            }
        },
        contract: {
            type: Object,
            default() {
                return null
            }
        }
    },
    data: function () {
        return {
            loading: false,
            results: [],
            materialsVisible: false,
            materialReport: null,
            fields: [
                { field: "reporting_date", title: this.$t('daily_summary.date'),  sort: true, headerClass: 'routa-table-header'},
                { field: "reporter_name", title: this.$t('material_reports.reporter'),  sort: true, headerClass: 'routa-table-header'},
                { field: "customer_name",title: this.$t('contracts.customer'),  sort: true, headerClass: 'routa-table-header'},
                { field: "contract_name", title: this.$t('orders.contract'),  sort: true, headerClass: 'routa-table-header'},
                { field: "material", title: this.$t('material_reports.material'),  sort: true, headerClass: 'routa-table-header'},
                { field: "amount", title: this.$t('material_reports.amount'),  sort: true, headerClass: 'routa-table-header', type: 'number'},
                { field: "unit", title: this.$t('material_reports.unit'),  sort: true, headerClass: 'routa-table-header'},
                { field: "info", title: this.$t('material_reports.info'),  sort: true, headerClass: 'routa-table-header'}
            ]
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    mounted() {
        this.fetchMaterialReports()
    },
    watch: {
        searchParams () {
            this.fetchMaterialReports()
        }
    },
    methods: {
        fetchMaterialReports: function () {
            this.results = []
            if (this.searchParams) {
                let mappedParams = {
                    contract: this.contract.id,
                    from: this.searchParams.dateFrom ? this.getDateIsoString(new Date(this.searchParams.dateFrom)) : null,
                    to: this.searchParams.dateTo ? this.getDateIsoString(new Date(this.searchParams.dateTo)) : null,
                }
                this.loading = true
                this.$emit('loading', true)
                this.restFetchParams(this.materialReportUrl, mappedParams, this.handleResponse)
            }
        },
        handleResponse: function (response) {
            if (response) {
                this.results = response.data
            }
            this.loading = false
            this.$emit('loading', false)
        },
        editMaterialReportItem: function (item) {
            this.materialReport = item

            /*
            this.materialReport = {
                ...item,
                contract: this.contract && this.contract.id && this.contract.id > 0 ? {
                    id: this.contract.id,
                    name: this.contract.name
                } : null,
                reporting_date: item.date ? item.date : null,
            }

             */
        },
        closeEditor() {
            this.materialReport = null
        },
        submitMaterialReport(item) {
            let jsonMaterialReport = this.setDefaultValues(item)
            if(jsonMaterialReport !== undefined) {
                this.loading = true
                if (jsonMaterialReport.id < 0) {
                    this.restAdd(this.simpleMaterialReportUrl, jsonMaterialReport, this.handleMaterialReportResponse, this.fail)
                } else {
                    this.restUpdate(this.simpleMaterialReportUrl, jsonMaterialReport, this.handleMaterialReportResponse, this.fail)
                }
            }
        },
        setDefaultValues: function (value) {
            let materialReport = JSON.parse(JSON.stringify(value))
            if (materialReport.material === '') {
                this.showAlert(this.$t('material_reports.invalid_material'))
                return undefined
            }
            if(materialReport.reporting_date) {
                materialReport.reporting_date = materialReport.reporting_date
            }
            if (materialReport.unit === '') {
                this.showAlert(this.$t('pricing_model_conversion.invalid_unit'))
                return undefined
            }

            if (materialReport.info === null || materialReport.info === '') {
                this.showAlert(this.$t('material_reports.info_missing'))
                return undefined
            }

            materialReport.consumption_records = [{
                id: materialReport.record_id && materialReport.record_id,
                material: materialReport.material,
                amount: materialReport.amount,
                unit: materialReport.unit,
                info: materialReport.info,
            }]
            return materialReport
        },
        fail: function () {
            this.loading = false
            this.showAlert(this.$t('common.alert_update_failed'))
        },
        downloadMaterialReportExcel: function () {
            this.loading = true
            this.restGetDownloadFile(this.materialReportExcelUrl,
                {...this.getCommonReportParams(this.searchParams),
                from: this.searchParams.dateFrom ? this.getDateIsoString(new Date(this.searchParams.dateFrom)) : null,
                to: this.searchParams.dateTo ? this.getDateIsoString(new Date(this.searchParams.dateTo)) : null},
                this.fileDownloadSuccess,
                this.fileDownloadFail)
        },
        fileDownloadSuccess: function (response) {
            this.loading = false
            this.downloadFile(response, this.$t('material_reports.file_name'))
        },
        fileDownloadFail: function () {
            this.loading = false
            this.showAlert(this.$t('common.report_generation_failed'))
        },
        handleMaterialReportResponse: function (response) {
            if (response) {
                this.loading = false
                this.materialReport = null
                this.fetchMaterialReports()
                this.materialsVisible = true
            }
        },
        showAddMaterialReportModal() {
            this.materialReport = this.getSimpleMaterialReport()
        }
    }
}
</script>

<style scoped>

</style>
